"use client"

import React from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowTurnDownLeft,
  faFileExport,
} from "@instadeepai/fontawesome-pro-regular"

import { Button, Card, Progress, TextArea } from "@shared/ui"

import { useForgeQuestionForm } from "../../hooks"
import { ForgeFormCompletion } from "./forge-form-completion"
import { Question } from "../../types"

interface ForgeFormProps {
  questions: Question[]
}

export function ForgeForm({ questions }: ForgeFormProps) {
  const { form, question, handlers, getCardStyle } = useForgeQuestionForm(questions)
  
  if (form.isCompleted) {
    return <ForgeFormCompletion />
  }

  return (
    <Card
      className={`flex h-[588px] w-[560px] flex-col gap-6 rounded-xl border p-8 transition-colors duration-200 ${getCardStyle()}`}
    >
      <h1 className="flex flex-grow flex-col justify-end text-[38px] font-semibold text-[#121926]">
        <div
          className={`transition-opacity duration-300 ${form.isFading ? "opacity-0" : "opacity-100"}`}
        >
          {question.currentQuestion?.text
            .split("<br />")
            .map((line: string, index: number) => (
              <React.Fragment key={index}>
                {line}
                {index <
                  (question.currentQuestion?.text.split("<br />").length || 0) -
                    1 && <br />}
              </React.Fragment>
            ))}
          {question.currentQuestion?.subtext && (
            <>
              <span className="ml-1 text-[24px] font-normal text-[#474F5D]">
                {question.currentQuestion.subtext}
              </span>
            </>
          )}
        </div>
      </h1>
      <div className="flex flex-col gap-1">
        <TextArea
          className="min-h-[155px] w-full rounded-md border-[#E4E4E4]"
          value={form.currentAnswer}
          onChange={handlers.handleAnswerChange}
          onKeyDown={handlers.handleKeyDown}
          invalid={form.hasError}
        />
        {form.hasError && (
          <p className="text-sm text-red-600">Please provide an answer.</p>
        )}
      </div>
      <div className="flex items-center gap-3">
        <Button
          rightIcon={<FontAwesomeIcon icon={faFileExport} />}
          onClick={handlers.handleNextQuestion}
        >
          {question.isLastQuestion ? "Submit" : "Next"}
        </Button>
        {!question.isLastQuestion && (
          <span className="flex items-center gap-2 text-md font-semibold text-[#0438C1]">
            Press Enter
            <FontAwesomeIcon icon={faArrowTurnDownLeft} />
          </span>
        )}
      </div>
      <div className="flex items-center gap-4">
        <Progress
          value={question.progressValue}
          variant="linear"
          className="h-2 w-full"
        />
        <p className="whitespace-nowrap text-[14px] font-medium text-[#576272]">
          {`${question.questionsLeft} more questions...`}
        </p>
      </div>
    </Card>
  )
}
